import { reportHandledError } from '../../../utils/bugsnag';

/**
 * Why turf could not be trusted with a route line.
 *
 * `repeated-points` — the line contains a zero-length segment (the same lng/lat twice in a
 * row). `non-finite-cursor` — the cursor position that came off the map event was NaN or
 * Infinite. Either one makes nearestPointOnLine throw "coordinates must contain numbers",
 * and both are now guarded; neither should ever happen.
 */
export type DegenerateHoverReason = 'repeated-points' | 'non-finite-cursor';

export interface DegenerateHoverDetails {
  /** Where it was seen, e.g. 'simpleHover' or 'routeLineHit'. */
  source: string;
  routeKey?: string | null;
  lineIndex?: number | null;
  pointCount?: number;
  keptCount?: number;
  lng?: number;
  lat?: number;
}

const reported = new Set<string>();

/**
 * Record the first time a route line turns up in a state the hover maths cannot use.
 *
 * Deliberately once per reason-route-line per page load. The hover handler runs on every
 * mousemove (~30/s) and the condition persists until the next render cleans it, so an
 * unconditional report would flood Bugsnag with the same event — which is what the original
 * crash did. Reporting is a no-op outside production (see utils/bugsnag).
 */
export function reportDegenerateHoverInput(
  reason: DegenerateHoverReason,
  details: DegenerateHoverDetails
): void {
  const key = `${reason}:${details.source}:${details.routeKey ?? '?'}:${details.lineIndex ?? '?'}`;
  if (reported.has(key)) return;
  reported.add(key);

  reportHandledError(new Error(`planner hover: ${reason}`), { reason, ...details });
}

/** Clears the once-per-page memory. Tests only. */
export function resetDegenerateHoverReports(): void {
  reported.clear();
}
